// @flow

import * as React from 'react';

import type { FormValuesShape } from 'final-form';
import { useField, useForm } from 'react-final-form';

import {
  ChoiceFieldGroup,
  ChoiceField,
  useBasicValidators,
} from '@gandi/form/FinalForm';
import { useTranslator } from '@gandi/translate';
import { Block, BlockContent, BlockHeading } from '@gandi/ui';

import Price from '../../../shared/Price';
import {
  CERT_ALTNAMES_INPUT_NAME,
  CERT_NUMBER_ADDRESSES_INPUT_NAME,
  CERT_PROVIDER_INPUT_NAME,
  CERT_TYPE_INPUT_NAME,
  CERT_TYPE_PROTECT_INPUT_NAME,
  CERT_WARRANTY_INPUT_NAME,
  choiceGroupProps,
} from '../../consts';
import { useMultiAdrressesPrices } from '../../hooks/prices';

export type NumberAddressesOptionProps = {
  readOnly?: boolean,
  process: 'create' | 'renew',
};

export function NumberAddressesOption({
  readOnly,
  process,
}: NumberAddressesOptionProps): React.MixedElement {
  const { __ } = useTranslator();
  const form = useForm<FormValuesShape>();

  const { required } = useBasicValidators();

  const {
    input: { value: provider },
    // $FlowFixMe[prop-missing]
  } = useField(CERT_PROVIDER_INPUT_NAME, { subscription: { value: true } });
  const {
    input: { value: type },
    // $FlowFixMe[prop-missing]
  } = useField(CERT_TYPE_INPUT_NAME, { subscription: { value: true } });
  const {
    input: { value: protect },
    // $FlowFixMe[prop-missing]
  } = useField(CERT_TYPE_PROTECT_INPUT_NAME, { subscription: { value: true } });
  const {
    input: { value: warranty },
    // $FlowFixMe[prop-missing]
  } = useField(CERT_WARRANTY_INPUT_NAME, { subscription: { value: true } });

  const {
    price3Addresses,
    price5Addresses,
    price10Addresses,
    price20Addresses,
  } = useMultiAdrressesPrices({ provider, type, protect, warranty, process });

  const items = [
    {
      value: '3',
      label: __('3 addresses'),
      additional: price3Addresses ? (
        <Price {...price3Addresses} showExcludeVatMention={false} />
      ) : undefined,
    },
    {
      value: '5',
      label: __('5 addresses'),
      additional: price5Addresses ? (
        <Price {...price5Addresses} showExcludeVatMention={false} />
      ) : undefined,
    },
    {
      value: '10',
      label: __('10 addresses'),
      additional: price10Addresses ? (
        <Price {...price10Addresses} showExcludeVatMention={false} />
      ) : undefined,
    },
    {
      value: '20',
      label: __('20 addresses'),
      additional: price20Addresses ? (
        <Price {...price20Addresses} showExcludeVatMention={false} />
      ) : undefined,
    },
  ];

  React.useEffect(() => {
    const unsubscribe = form.subscribe(
      ({ values }) => {
        if (!values || !values[CERT_NUMBER_ADDRESSES_INPUT_NAME]) {
          // init
          form.batch(() => {
            form.change(CERT_NUMBER_ADDRESSES_INPUT_NAME, '3');
            // display an empty field only on creation
            form.change(CERT_ALTNAMES_INPUT_NAME, ['']);
          });
        }
      },
      {
        values: true,
      },
    );

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Block>
      <BlockHeading>{__('How many addresses?')}</BlockHeading>
      <BlockContent>
        <ChoiceFieldGroup
          name={CERT_NUMBER_ADDRESSES_INPUT_NAME}
          validate={required}
          choiceGroupProps={choiceGroupProps}
        >
          {items.map(({ value, label, additional }) => (
            <ChoiceField
              key={value}
              name={CERT_NUMBER_ADDRESSES_INPUT_NAME}
              value={value}
              label={label}
              disabled={readOnly}
            >
              {!!additional && <div slot="additional">{additional}</div>}
            </ChoiceField>
          ))}
        </ChoiceFieldGroup>
      </BlockContent>
    </Block>
  );
}
